<script>
    import { createEventDispatcher, onMount } from 'svelte';
    import { slide } from 'svelte/transition';
    import { settingsStore } from '../../stores/settingsStore.js';
    import { termsStore } from '../../stores/editorsStore.js';

    const dispatch = createEventDispatcher();
    
    let showDropdown = false;
    let selectedType = 'provisional'; // Default selection
    let selectedJurisdiction = 'US'; // Default jurisdiction for national phase
    
    const patentTypes = [
        { value: 'provisional', label: 'Provisional', icon: 'fa-file-alt' },
        { value: 'non-provisional', label: 'Non-Provisional', icon: 'fa-file-contract' },
        { 
            value: 'national-phase', 
            label: 'National Phase', 
            icon: 'fa-globe',
            jurisdictions: ['US', 'Canada', 'Europe', 'China', 'Japan', 'Korea', 'Singapore', 'Taiwan', 'Israel']
        }
    ];

    let showJurisdictionTooltip = false;
    let jurisdictionTooltipTimeout;
    
    // Load saved patent type and jurisdiction from settings
    onMount(() => {
        const settings = $settingsStore;
        if (settings.patentType) {
            selectedType = settings.patentType;
        }
        if (settings.jurisdiction) {
            selectedJurisdiction = settings.jurisdiction;
        }
    });

    // React to settings store changes (e.g., when loading a file)
    $: {
        // Always sync with settings store, including when reset to defaults
        const storePatentType = $settingsStore.patentType || 'provisional';
        if (selectedType !== storePatentType) {
            console.log('[PatentTypeDropdown] Settings changed, updating selectedType:', storePatentType);
            selectedType = storePatentType;
        }
        // Only update jurisdiction if patent type is national-phase
        if (selectedType === 'national-phase') {
            if ($settingsStore.jurisdiction && $settingsStore.jurisdiction !== selectedJurisdiction) {
                console.log('[PatentTypeDropdown] Settings changed, updating selectedJurisdiction:', $settingsStore.jurisdiction);
                selectedJurisdiction = $settingsStore.jurisdiction;
            }
        } else {
            // Clear jurisdiction if not national phase
            if (selectedJurisdiction !== null) {
                selectedJurisdiction = null;
            }
        }
    }
    
    function handleTypeSelect(type) {
        console.log('[PatentTypeDropdown] Selecting type:', type);
        selectedType = type;
        showDropdown = false;
        
        // Reset jurisdiction if switching away from national phase
        if (type !== 'national-phase') {
            selectedJurisdiction = null; // Clear jurisdiction for non-national phase types
        } else if (!selectedJurisdiction) {
            selectedJurisdiction = 'US'; // Default to US if switching to national phase without a jurisdiction
        }
        
        // Save to settings store
        settingsStore.update(store => {
            console.log('[PatentTypeDropdown] Current store:', store);
            const newStore = {
                ...store,
                patentType: type,
                jurisdiction: type === 'national-phase' ? selectedJurisdiction : null
            };
            console.log('[PatentTypeDropdown] New store:', newStore);
            return newStore;
        });

        // Recalculate claim dependencies based on new patent type
        const isProvisional = type === 'provisional';
        console.log('[PatentTypeDropdown] Recalculating claim dependencies, isProvisional:', isProvisional);
        termsStore.recalculateClaimDependencies(isProvisional);

        // Dispatch event for any listeners
        dispatch('patentTypeChange', { type, jurisdiction: type === 'national-phase' ? selectedJurisdiction : null });
    }
    
    function handleClickOutside(event) {
        if (!event.target.closest('.patent-type-dropdown-container')) {
            showDropdown = false;
        }
    }
    
    function handleKeydown(e) {
        if (e.key === 'Escape') {
            showDropdown = false;
        }
    }

    function handleNationalPhaseHover() {
        clearTimeout(jurisdictionTooltipTimeout);
        showJurisdictionTooltip = true;
    }

    function handleNationalPhaseLeave() {
        jurisdictionTooltipTimeout = setTimeout(() => {
            showJurisdictionTooltip = false;
        }, 300); // Small delay to allow moving to tooltip
    }

    function handleJurisdictionSelect(jurisdiction, event) {
        event.stopPropagation(); // Prevent triggering the parent menu item
        console.log('[PatentTypeDropdown] Selecting jurisdiction:', jurisdiction);
        
        // Selecting a jurisdiction automatically sets patent type to national-phase
        selectedType = 'national-phase';
        selectedJurisdiction = jurisdiction;
        showJurisdictionTooltip = false;
        showDropdown = false;
        
        // Save both patent type and jurisdiction to settings store
        settingsStore.update(store => {
            console.log('[PatentTypeDropdown] Setting national-phase with jurisdiction:', jurisdiction);
            return {
                ...store,
                patentType: 'national-phase',
                jurisdiction: jurisdiction
            };
        });

        // Recalculate claim dependencies - national-phase is treated like non-provisional
        console.log('[PatentTypeDropdown] Recalculating claim dependencies for national-phase (non-provisional)');
        termsStore.recalculateClaimDependencies(false);

        // Dispatch events for any listeners
        dispatch('patentTypeChange', { type: 'national-phase', jurisdiction });
        dispatch('jurisdictionChange', { jurisdiction });
    }
    
    // Get the current selected type label and icon
    $: currentTypeData = patentTypes.find(t => t.value === selectedType) || { label: 'Patent Type', icon: 'fa-file' };
    $: currentTypeLabel = selectedType === 'national-phase' && selectedJurisdiction ? `${currentTypeData.label} (${selectedJurisdiction})` : currentTypeData.label;
    $: currentTypeIcon = currentTypeData.icon;
</script>

<svelte:window on:click={handleClickOutside} on:keydown={handleKeydown} />

<div class="patent-type-dropdown-container">
    <button 
        class="toolbar-button patent-type-button"
        on:click={() => showDropdown = !showDropdown}
        title="Select patent type"
        aria-expanded={showDropdown}
        aria-haspopup="true"
        data-testid="patent-type-button"
    >
        <i class="fas {currentTypeIcon}"></i>
        <span class="button-text">{currentTypeLabel}</span>
        <i class="fas fa-chevron-down dropdown-arrow"></i>
    </button>
    
    {#if showDropdown}
        <div 
            class="patent-dropdown" 
            transition:slide|local
            role="menu"
            tabindex="-1"
        >
            {#each patentTypes as type (type.value)}
                <button 
                    class="menu-item"
                    class:active={selectedType === type.value}
                    class:no-click={type.value === 'national-phase'}
                    on:click={type.value !== 'national-phase' ? () => handleTypeSelect(type.value) : null}
                    on:mouseenter={type.value === 'national-phase' ? handleNationalPhaseHover : null}
                    on:mouseleave={type.value === 'national-phase' ? handleNationalPhaseLeave : null}
                    role="menuitem"
                    data-testid="patent-type-{type.value}"
                >
                    <i class="fas {type.icon}"></i>
                    <span>{type.label}</span>
                    {#if selectedType === type.value}
                        <i class="fas fa-check menu-item-check"></i>
                    {/if}
                    
                    {#if type.value === 'national-phase' && showJurisdictionTooltip}
                        <div 
                            class="jurisdiction-tooltip"
                            on:mouseenter={() => clearTimeout(jurisdictionTooltipTimeout)}
                            on:mouseleave={handleNationalPhaseLeave}
                        >
                            <div class="jurisdiction-list">
                                {#each type.jurisdictions as jurisdiction}
                                    <button 
                                        class="jurisdiction-item"
                                        class:active={selectedType === 'national-phase' && selectedJurisdiction === jurisdiction}
                                        on:click={(event) => handleJurisdictionSelect(jurisdiction, event)}
                                    >
                                        {jurisdiction}
                                        {#if selectedType === 'national-phase' && selectedJurisdiction === jurisdiction}
                                            <i class="fas fa-check jurisdiction-check"></i>
                                        {/if}
                                    </button>
                                {/each}
                            </div>
                        </div>
                    {/if}
                </button>
            {/each}
        </div>
    {/if}
</div>

<style>
.patent-type-dropdown-container {
    position: relative;
    display: inline-block;
}

.patent-type-button {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 6px;
    min-width: fit-content;
}

.dropdown-arrow {
    margin-left: 2px;
    font-size: 0.75em;
    transition: transform 0.2s ease;
}

.patent-type-button[aria-expanded="true"] .dropdown-arrow {
    transform: rotate(180deg);
}

.patent-dropdown {
    position: absolute;
    top: calc(100% + var(--spacing-2));
    left: 0;
    z-index: var(--z-index-dropdown);
    min-width: 180px;
    padding: var(--spacing-2);
    background-color: var(--color-bg-file-dropdown);
    border-radius: var(--border-radius-2xl);
    box-shadow: var(--shadow-dropdown);
}

.menu-item {
    position: relative;
    display: flex;
    align-items: center;
    width: 100%;
    padding: var(--spacing-2) var(--spacing-4);
    font-size: var(--font-size-sm);
    white-space: nowrap;
    color: var(--color-text-secondary);
    background: transparent;
    border: none;
    border-radius: var(--border-radius-xl);
    transition: all 0.2s var(--ease-in-out);
    cursor: pointer;
    gap: var(--spacing-2);
}

.menu-item:hover {
    background-color: var(--color-bg-hover-light);
}

.menu-item i:first-child {
    width: var(--spacing-4);
}

.menu-item.active {
    color: var(--color-text-primary);
    background-color: var(--color-bg-hover-medium);
}

.menu-item.no-click {
    cursor: default;
}

.menu-item.no-click:hover {
    background-color: var(--color-bg-hover-light);
}

.menu-item-check {
    margin-left: auto;
    color: var(--color-terms-600);
}

.jurisdiction-tooltip {
    position: absolute;
    top: 0;
    left: 100%;
    z-index: calc(var(--z-index-dropdown) + 1);
    min-width: 120px;
    padding: var(--spacing-3);
    margin-left: var(--spacing-2);
    white-space: nowrap;
    background-color: var(--color-bg-file-dropdown);
    border-radius: var(--border-radius-xl);
    box-shadow: var(--shadow-dropdown);
}

.jurisdiction-list {
    display: flex;
    flex-direction: column;
    gap: var(--spacing-1);
}

.jurisdiction-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
    padding: var(--spacing-1) var(--spacing-2);
    font-size: var(--font-size-xs);
    text-align: left;
    color: var(--color-text-secondary);
    background: transparent;
    border: none;
    border-radius: var(--border-radius-lg);
    transition: all 0.2s var(--ease-in-out);
    cursor: pointer;
}

.jurisdiction-item:hover {
    color: var(--color-text-primary);
    background-color: var(--color-bg-hover-light);
}

.jurisdiction-item.active {
    color: var(--color-text-primary);
    background-color: var(--color-bg-hover-medium);
}

.jurisdiction-check {
    margin-left: var(--spacing-1);
    font-size: var(--font-size-xs);
    color: var(--color-terms-600);
}

/* Dark mode adjustments */
:global([data-theme="dark"]) .patent-type-button {
    color: var(--color-text-primary);
}

/* Responsive design - hide text on smaller viewports */
@media (width <= 1600px) {
    .patent-type-button .button-text {
        font-size: var(--font-size-xs);
    }
}

@media (width <= 1400px) {
    .patent-type-button .button-text {
        display: none;
    }
    
    .patent-type-button {
        padding: 0 var(--spacing-2);
    }
}
</style>